/**
 * Workflow Triggers API client (P3-E Doc 40).
 */
import { api } from "./client";

export type TriggerType = "webhook" | "cron";

export interface WorkflowTrigger {
  id: string;
  workflow_code: string;
  trigger_type: TriggerType;
  slug: string;
  name: string;
  enabled: boolean;
  config: Record<string, unknown>;
  last_fired_at: string | null;
  last_run_id: string | null;
  fire_count: number;
  last_error: string | null;
  cron_human?: string | null;
  webhook_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebhookSecretReveal {
  slug: string;
  secret: string;
  webhook_url: string;
  sample_curl: string;
}

export interface CronPreset {
  key: string;
  expr: string;
  label: string;
}

export interface CreateTriggerRequest {
  workflow_code: string;
  trigger_type: TriggerType;
  slug: string;
  name: string;
  enabled?: boolean;
  config?: Record<string, unknown>;
}

export interface UpdateTriggerRequest {
  name?: string | null;
  enabled?: boolean | null;
  config?: Record<string, unknown> | null;
}

export interface TestFireResponse {
  ok: boolean;
  run_id: string | null;
  status: string;
  error: string | null;
}

export const triggersApi = {
  listPresets: () =>
    api.get<CronPreset[]>("/api/v1/admin/workflow-triggers/cron-presets"),

  list: (workflowCode?: string) =>
    api.get<{ items: WorkflowTrigger[] }>(
      "/api/v1/admin/workflow-triggers",
      workflowCode ? { query: { workflow_code: workflowCode } } : undefined,
    ),

  /** Create — returns WebhookSecretReveal cho webhook (1-time secret), TriggerOut cho cron */
  create: (body: CreateTriggerRequest) =>
    api.post<WebhookSecretReveal | WorkflowTrigger>(
      "/api/v1/admin/workflow-triggers",
      body,
    ),

  update: (id: string, body: UpdateTriggerRequest) =>
    api.patch<WorkflowTrigger>(
      `/api/v1/admin/workflow-triggers/${encodeURIComponent(id)}`,
      body,
    ),

  delete: (id: string) =>
    api.delete<void>(
      `/api/v1/admin/workflow-triggers/${encodeURIComponent(id)}`,
    ),

  testFire: (id: string) =>
    api.post<TestFireResponse>(
      `/api/v1/admin/workflow-triggers/${encodeURIComponent(id)}/test-fire`,
    ),

  regenerateSecret: (id: string) =>
    api.post<WebhookSecretReveal>(
      `/api/v1/admin/workflow-triggers/${encodeURIComponent(id)}/regenerate-secret`,
    ),
};

/** Helper detect if response is WebhookSecretReveal (has secret field). */
export function isWebhookReveal(
  res: WebhookSecretReveal | WorkflowTrigger,
): res is WebhookSecretReveal {
  return "secret" in res && "sample_curl" in res;
}
